import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual, useSelector } from 'react-redux';
import i18next from 'i18next';

import { PaymentMethodCard } from './PaymentMethodCard';
import { ViewMoreButton } from '../ViewMoreButton';
import { BillingInformation } from './BillingInformation';
import { BillingPremiumCostFigo } from './BillingPremiumCostFigo';
import { BillingPremiumCostOrca } from './BillingPremiumCostOrca';
import {
  UpdatePaymentMethodModal,
} from '../../myAccount/BillingAndPayments/UpdatePaymentMethodModal';

import {
  PAYMENT_PLANS,
  POLICY_STATUS,
  getBillingCycleType,
} from '../../../constants';
import {
  MARKETING_CHANNEL_ID,
  PPU_MARKETING_CHANNELS,
} from '../../../theme/theme.constants';
import {
  useBillingAndPayments,
} from '../../myAccount/BillingAndPayments/hooks/useBillingAndPayments';

import { formatShortDateByLanguage } from '../../../services/language.utils';
import { useMoneris, usePaymentech } from '../../../hooks';

import './BillingAndPayments.css';
import {
  CURRENCY_FORMAT_DEFAULT,
  GLOBAL_LANGUAGES,
  LANGUAGES_FR_CA,
} from '../../../i18n';
import { MONERIS_MARKET_CHANNELS, PAYMENTECH_MARKET_CHANNELS } from '../../myAccount/BillingAndPayments/billingAndPayments.constants';

export const BillingAndPayments = ({
  company = MARKETING_CHANNEL_ID.figo,
  isGPI = false,
  selectedPolicy = {},
}) => {
  const store = useSelector(({
    billingPayments,
    policies,
  }) => ({ billingPayments, policies }), shallowEqual);
  const { t } = useTranslation('insurance');
  const usePaymentechFlag = usePaymentech();
  const useMonerisFlag = useMoneris();

  /**
   * Temporary fix. TODO: get payment method id instead.
   */
  const PAYMENT_METHOD_NAMES_MULTILANGUAGE = [
    i18next.t('common:paymentMethodNames.bank'),
    i18next.t('common:paymentMethodNames.card'),
    i18next.t('common:paymentMethodNames.agencyBill'),
  ];

  const {
    NextBillingDate,
    PolicyDisplayNumber,
    Status,
  } = selectedPolicy || {};

  const isFigoPolicy = useMemo(() => (
    company && company === MARKETING_CHANNEL_ID.figo
  ), [company]);

  const isPPUPolicy = useMemo(() => (
    company && PPU_MARKETING_CHANNELS.includes(company)
  ), [company]);

  const billingInformationFigo = useMemo(() => {
    if (!selectedPolicy) {
      return {
        billingName: '',
        paymentPlan: 0,
      };
    }

    const policyFound = store.policies.allPolicies
      .find((policy) => policy.Policy.Number === PolicyDisplayNumber) || {};

    let billingName = '';
    const { billingDetails } = store.billingPayments;

    if (billingDetails && (billingDetails.CCFirstName
      || billingDetails.CCLastName)) {
      billingName = `${billingDetails.CCFirstName} `
        + `${billingDetails.CCLastName}`;
    }

    return {
      ...policyFound.BillingInformation || {},
      billingName,
      paymentPlan: policyFound.Policy?.PaymentPlan,
    };
  }, [store.policies, PolicyDisplayNumber, store.billingPayments]);

  const billingInformationOrca = useMemo(() => {
    if (!selectedPolicy) {
      return {
        billingName: '',
        bottomInfo: [],
        paymentPlan: 0,
        topInfo: [],
        totalPayAmount: 0,
      };
    }

    // TODO: billing cycle enum should be from back 1 or 0
    // instead "Annual"/"Monthly"
    const BILLING_CYCLE = getBillingCycleType();

    const coverages = selectedPolicy.PetCoveragesByPet || {};
    // matches integer value from Figo enum
    const paymentPlan = selectedPolicy?.BillingCycle === BILLING_CYCLE
      .monthly ? PAYMENT_PLANS.monthly : PAYMENT_PLANS.annual;

    let billingName = '';
    const { PaymentOption } = selectedPolicy;

    if (PaymentOption && (PaymentOption.FirstNameOnCard
      || PaymentOption.LastNameOnCard)) {
      billingName = `${PaymentOption.FirstNameOnCard} `
        + `${PaymentOption.LastNameOnCard}`;
    }

    const premiumCosts = {
      Amount: coverages.PaymentBreakdown?.TotalPremiumCost,
      Name: t('billingInformation.premiumCosts'),
    };

    return {
      billingName,
      bottomInfo: [
        premiumCosts,
        ...(selectedPolicy.CostLineItems || [])],
      paymentPlan,
      premiumCosts,
      topInfo: coverages.CostLineItems || [],
      totalPayAmount: coverages.PaymentBreakdown?.TotalPayAmount,
    };
  }, [selectedPolicy, t]);

  const currencyFormat = useMemo(() => {
    let value = CURRENCY_FORMAT_DEFAULT;

    if (!selectedPolicy) {
      return value;
    }

    if (selectedPolicy.MarketChannelId === MARKETING_CHANNEL_ID.ppu) {
      // PPU currency format always in Canadian USD
      // TODO: currency should come from the backend pending
      value = GLOBAL_LANGUAGES.find(({ key }) => key === LANGUAGES_FR_CA);
    }

    return value;
  }, [selectedPolicy]);

  const {
    ExpirationMonth,
    ExpirationYear,
    LastFour,
    PaymentMethodName,
  } = selectedPolicy?.PaymentOption || {};

  const hasPaymentMethod = PAYMENT_METHOD_NAMES_MULTILANGUAGE
    .includes(PaymentMethodName);
  const isRedirectAvailable = useMemo(() => (
    Status === POLICY_STATUS.active
    || (Status === POLICY_STATUS.future
      && isFigoPolicy)
  ), [Status, isFigoPolicy]);

  const {
    onOpenPaymentMethod,
    hideUpdatePaymentMethodModal,
    updatePaymentModalVisible,
  } = useBillingAndPayments({ policyInfo: selectedPolicy || {} });

  const paymentMethodCardCases = {
    [PAYMENT_METHOD_NAMES_MULTILANGUAGE[0]]: {
      lastNumbers: LastFour,
      methodType: 'savingBank',
    },
    [PAYMENT_METHOD_NAMES_MULTILANGUAGE[1]]: {
      expirationDate: ExpirationMonth && ExpirationYear
        ? `${ExpirationMonth.toString().padStart(2, '0')}/${ExpirationYear}`
        : '',
      lastNumbers: LastFour,
      methodType: 'creditCard',
    },
    default: {
      isRedirectAvailable,
      methodType: 'addPaymentMethod',
      onAddPaymentMethod: onOpenPaymentMethod,
    },
  };

  const paymentMethodCardProps = paymentMethodCardCases[PaymentMethodName]
    || paymentMethodCardCases.default;

  if (selectedPolicy?.Status === POLICY_STATUS.cancelled
    || selectedPolicy?.Status === POLICY_STATUS.past) {
    return null;
  }

  const showEditPaymentMethodButton = useMemo(() => {
    const baseCondition = hasPaymentMethod && isRedirectAvailable;

    if (PAYMENTECH_MARKET_CHANNELS.includes(company) && usePaymentechFlag) {
      return baseCondition;
    }

    if (MONERIS_MARKET_CHANNELS.includes(company) && useMonerisFlag) {
      return baseCondition;
    }

    // no flags turned on, so return the original condition
    return baseCondition && !isPPUPolicy;
  }, [
    hasPaymentMethod,
    isPPUPolicy,
    isRedirectAvailable,
    useMonerisFlag,
    usePaymentechFlag,
  ]);

  return (
    <div className="Billing-payments__container">
      <div className="Billing-payments__header">
        <h3 className="Billing-payments__title">{t('billingAndPayments')}</h3>

        {showEditPaymentMethodButton && (
          <ViewMoreButton
            onClick={onOpenPaymentMethod}
            text={t('viewBillingDetails')}
          />
        )}
      </div>

      {hasPaymentMethod && (
        <div className="Billing-payments__body">
          <div className="Billing-payments-general">
            {isFigoPolicy ? (
              <BillingPremiumCostFigo
                annualTotal={billingInformationFigo.PayAmount}
                isGPI={isGPI}
                monthlyInstallmentFee={billingInformationFigo.InstallmentFee}
                monthlyPremiumCost={billingInformationFigo.PayAmount}
                monthlyTotal={billingInformationFigo.TotalPayAmount}
                paymentPlan={billingInformationFigo.paymentPlan}
              />
            ) : (
              <BillingPremiumCostOrca
                bottomInfo={billingInformationOrca.bottomInfo}
                currencyFormat={currencyFormat}
                isPPUPolicy={isPPUPolicy}
                paymentPlan={billingInformationOrca.paymentPlan}
                premiumCosts={billingInformationOrca.premiumCosts}
                topInfo={billingInformationOrca.topInfo}
                totalPayAmount={billingInformationOrca.totalPayAmount}
              />
            )}

            <BillingInformation
              name={isFigoPolicy
                ? billingInformationFigo.billingName
                : billingInformationOrca.billingName}
              nextBillingDate={isPPUPolicy ? ''
                : formatShortDateByLanguage(NextBillingDate)}
              paymentPlan={isFigoPolicy
                ? billingInformationFigo.paymentPlan
                : billingInformationOrca.paymentPlan}
            />
          </div>

          {company !== MARKETING_CHANNEL_ID.figo && (
            // TODO: Add this block when the Monthly Premiums data is ready
            <></>
          )}
        </div>
      )}

      <div className="Billing-payments__footer">
        <h5>{t('paymentMethod')}</h5>

        <PaymentMethodCard {...paymentMethodCardProps} />

        <UpdatePaymentMethodModal
          onClose={hideUpdatePaymentMethodModal}
          show={updatePaymentModalVisible}
        />
      </div>
    </div>
  );
};
